import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from '@/components/ui/dialog';
import { X } from 'lucide-react';
import apiCoreAdapter from '../../../hooks/apiCoreAdapter';
import { useContactChannel } from '../../../hooks/useContactChannel';

interface PatientLimitContactModalProps {
  open: boolean;
  onClose: () => void;
  attemptedValue: number;
  /** Límite interno — se usa junto con dropPercentage para el copy. */
  processLimit: number | null;
  /** % de baja ya calculado (redondeado) respecto de processLimit, ej. 42 para "42%". */
  dropPercentage: number;
}

export const PatientLimitContactModal: React.FC<PatientLimitContactModalProps> = ({
  open,
  onClose,
  attemptedValue,
  processLimit,
  dropPercentage,
}) => {
  // NZ, u O que viene de NZ: este mensaje de fricción tiene que dirigir siempre a
  // administración/NZ (WhatsApp de NZ, no el de CX de F/N/resto de O).
  const { email: SUPPORT_EMAIL } = useContactChannel();
  const [consentChecked, setConsentChecked] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const handleClose = () => {
    setConsentChecked(false);
    setIsSending(false);
    onClose();
  };

  const titleText = 'Este cambio no se puede autogestionar';

  // Mismo tono y estructura que el modal autogestionable ("Antes de confirmar este cambio"):
  // única diferencia real es que a partir del 30% de baja no se puede autogestionar.
  const reasonText = (
    <>
      Notamos que tu nueva preferencia queda un <strong>{dropPercentage}%</strong> por debajo
      del límite que tenemos definido para vos
      {processLimit != null ? <> (<strong>{processLimit} pacientes</strong>)</> : null}.
      Una baja de este tamaño no se puede autogestionar desde tu perfil: necesitamos que lo
      gestiones directamente con el equipo.
    </>
  );

  const handleContact = async () => {
    if (isSending) return;
    setIsSending(true);
    const contactNow = new Date();
    const fechaConsentimiento =
      contactNow.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) +
      ' a las ' +
      contactNow.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    const limitConsentHtml = `<!DOCTYPE html>
<html lang="es">
<head><meta charset="UTF-8" /><meta name="viewport" content="width=device-width, initial-scale=1.0" /></head>
<body style="margin:0;padding:0;background-color:#f0f0f0;font-family:Arial,Helvetica,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:#f0f0f0;padding:32px 0;">
    <tr><td align="center">
      <table width="560" cellpadding="0" cellspacing="0" border="0" style="background-color:#ffffff;border-radius:12px;overflow:hidden;max-width:560px;width:100%;">
        <tr>
          <td align="center" style="background-color:#4C1D95;padding:28px 32px;">
            <p style="margin:0;color:#ffffff;font-size:20px;font-weight:700;letter-spacing:0.3px;font-family:Arial,Helvetica,sans-serif;">
              Solicitud de cambio de límite registrada ✅
            </p>
          </td>
        </tr>
        <tr>
          <td style="padding:32px 36px 8px;">
            <p style="margin:0 0 12px;font-size:15px;color:#1a1f36;font-family:Arial,Helvetica,sans-serif;">Hola,</p>
            <p style="margin:0 0 24px;font-size:15px;color:#444444;line-height:1.6;font-family:Arial,Helvetica,sans-serif;">
              Te enviamos este mail para dejar registro de que el <strong>${fechaConsentimiento}</strong>
              solicitaste un cambio en tu límite preferido de pacientes.
            </p>
            <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:#F5F3FF;border-left:4px solid #7C3AED;border-radius:0 8px 8px 0;margin-bottom:24px;">
              <tr><td style="padding:16px 20px;">
                <p style="margin:0 0 8px;font-size:12px;font-weight:700;color:#7C3AED;text-transform:uppercase;letter-spacing:0.8px;font-family:Arial,Helvetica,sans-serif;">Cambio solicitado</p>
                <p style="margin:0;font-size:15px;color:#1a1f36;font-family:Arial,Helvetica,sans-serif;">
                  Nuevo tope solicitado: <strong>${attemptedValue} pacientes</strong>
                  ${processLimit != null ? `<br>Baja respecto del límite interno (${processLimit} pacientes): <strong>${dropPercentage}%</strong>` : ''}
                </p>
              </td></tr>
            </table>
            <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:#FFFBEB;border:1px solid #FCD34D;border-radius:8px;margin-bottom:24px;">
              <tr><td style="padding:16px 20px;">
                <p style="margin:0;font-size:13px;color:#92400E;line-height:1.6;font-style:italic;font-family:Arial,Helvetica,sans-serif;">
                  "Entiendo que este cambio requiere coordinación con el equipo y que mi cupo actual
                  permanecerá sin cambios hasta recibir confirmación."
                </p>
              </td></tr>
            </table>
            <p style="margin:0 0 24px;font-size:14px;color:#555555;line-height:1.6;font-family:Arial,Helvetica,sans-serif;">
              El equipo se pondrá en contacto para coordinar el cambio. Si tenés dudas escribinos a
              <a href="mailto:${SUPPORT_EMAIL}" style="color:#7C3AED;font-weight:700;text-decoration:none;">${SUPPORT_EMAIL}</a>.
            </p>
          </td>
        </tr>
        <tr><td style="padding:8px 36px 28px;"><p style="margin:0;font-size:13px;font-style:italic;color:#1a1f36;font-weight:700;font-family:Arial,Helvetica,sans-serif;">Equipo Psi Mammoliti</p></td></tr>
        <tr><td style="padding:0 36px 16px;"><p style="margin:0;font-size:12px;color:#888888;font-family:Arial,Helvetica,sans-serif;line-height:1.5;">Por favor no respondas este mensaje, ya que es automático. Cualquier inquietud podés contactarte a <a href="mailto:${SUPPORT_EMAIL}" style="color:#888888;text-decoration:underline;">${SUPPORT_EMAIL}</a>.</p></td></tr>
        <tr><td style="padding:0 36px 24px;"><hr style="border:none;border-top:1px solid #e8e8e8;margin:0 0 16px;" /><p style="margin:0;font-size:11px;color:#aaaaaa;text-align:center;font-family:Arial,Helvetica,sans-serif;"><a href="#" style="color:#aaaaaa;text-decoration:underline;">Click here to unsubscribe</a></p></td></tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`;
    try {
      await apiCoreAdapter.psychologistNotifyProfileChanges({
        html: limitConsentHtml,
        subject: 'Solicitud de cambio de límite de pacientes',
        from: 'no-responder@psimammoliti.com',
      });
    } catch {
      // El toast de error ya fue mostrado por secureApiPost
      setIsSending(false);
      return;
    }

    // También abrir mailto para notificar al equipo (comportamiento existente)
    const subject = encodeURIComponent('Solicitud de cambio de tope de pacientes');
    const body = encodeURIComponent(
      `Hola,\n\nQuiero coordinar un cambio en mi tope de pacientes preferidos a ${attemptedValue}.\n\nGracias.`,
    );
    window.open(`mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`, '_blank');
    handleClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="sm:max-w-md w-full p-0 overflow-hidden">
        <div className="px-6 pt-6 pb-4 relative" style={{ background: 'linear-gradient(180deg, #f5f3ff 0%, #ffffff 100%)' }}>
          <DialogClose className="absolute top-4 right-4 rounded-full w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
            <X size={16} />
          </DialogClose>
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold" style={{ color: '#1e1b4b' }}>
              {titleText}
            </DialogTitle>
          </DialogHeader>
        </div>

        <div className="px-6 pb-2 flex flex-col gap-4">
          <p className="text-sm text-gray-700 leading-relaxed m-0">
            {reasonText}
          </p>
          <p className="text-sm text-gray-600 leading-relaxed m-0">
            Escribinos a{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#6D57BC] font-medium hover:underline">{SUPPORT_EMAIL}</a>{' '}
            y vemos cómo ajustar tu cupo.
          </p>
          <div className="rounded-2xl bg-amber-50 border border-amber-200 px-5 py-4">
            <p className="text-xs text-amber-700 leading-relaxed italic m-0">
              &quot;Entiendo que este cambio requiere coordinación con el equipo y que mi cupo actual
              permanecerá sin cambios hasta recibir confirmación.&quot;
            </p>
          </div>
          <label htmlFor="limit-consent-checkbox" className="flex items-start gap-3 cursor-pointer select-none">
            <input
              id="limit-consent-checkbox"
              type="checkbox"
              checked={consentChecked}
              onChange={(e) => setConsentChecked(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 accent-purple-600 cursor-pointer flex-shrink-0"
            />
            <span className="text-sm text-gray-700">Estoy de acuerdo con lo informado arriba</span>
          </label>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex flex-col gap-2 mt-4">
          <button
            type="button"
            className="btn-purple w-full"
            onClick={handleClose}
          >
            Cambiar a otra cantidad
          </button>
          <button
            type="button"
            className={`text-sm transition-colors py-1 ${consentChecked && !isSending ? 'text-gray-500 hover:text-gray-700' : 'text-gray-300 cursor-not-allowed'}`}
            onClick={consentChecked && !isSending ? handleContact : undefined}
            disabled={!consentChecked || isSending}
          >
            {isSending ? 'Enviando...' : 'Contactarme'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PatientLimitContactModal;
